import { ChangeEvent, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { downscaleImage } from '../lib/imageDownscale';

/**
 * Reusable photo gallery field. Used by NewCar and EditCar.
 *
 * Renders:
 * - An "add photos" button (camera icon) that opens a file picker
 * - A grid of existing (URL) thumbnails with delete buttons
 * - A grid of newly-selected (File) thumbnails with delete buttons
 *
 * The parent owns all state. Pass `existingPhotos` only in the edit flow.
 */

export interface PhotoGalleryFieldProps {
  label: string;
  addButtonLabel: string;
  existingPhotos?: string[];
  onRemoveExisting?: (url: string) => void;
  newPhotos: File[];
  onAdd: (files: File[]) => void;
  onRemoveNew: (idx: number) => void;
  cols?: number;
}

export function PhotoGalleryField({
  label,
  addButtonLabel,
  existingPhotos,
  onRemoveExisting,
  newPhotos,
  onAdd,
  onRemoveNew,
  cols = 2,
}: PhotoGalleryFieldProps) {
  const { t } = useTranslation();
  const [preparing, setPreparing] = useState(false);

  const handleFilesSelected = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      const selected = Array.from(files);
      e.target.value = '';
      setPreparing(true);
      try {
        onAdd(await Promise.all(selected.map(downscaleImage)));
      } finally {
        setPreparing(false);
      }
    },
    [onAdd]
  );

  const hasExisting = existingPhotos && existingPhotos.length > 0;
  const hasNew = newPhotos.length > 0;

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">{label}</Typography>

      <Button variant="outlined" component="label" startIcon={<PhotoCamera />} disabled={preparing}>
        {preparing ? t('common.preparingPhotos') : addButtonLabel}
        <input
          type="file"
          hidden
          multiple
          accept="image/*"
          onChange={handleFilesSelected}
        />
      </Button>

      {hasExisting && existingPhotos && onRemoveExisting && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t('editCar.existingPhotos')}
          </Typography>
          <ImageList cols={cols} gap={8} sx={{ m: 0 }}>
            {existingPhotos.map((url) => (
              <ImageListItem key={url}>
                <img src={url} alt="" loading="lazy" />
                <ImageListItemBar
                  actionIcon={
                    <IconButton
                      sx={{ color: 'white' }}
                      onClick={() => onRemoveExisting(url)}
                      aria-label={t('common.delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}

      {hasNew && (
        <Box>
          {hasExisting && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('editCar.newPhotos')}
            </Typography>
          )}
          <ImageList cols={cols} gap={8} sx={{ m: 0 }}>
            {newPhotos.map((file, idx) => (
              <ImageListItem key={idx}>
                <img src={URL.createObjectURL(file)} alt={file.name} loading="lazy" />
                <ImageListItemBar
                  actionIcon={
                    <IconButton
                      sx={{ color: 'white' }}
                      onClick={() => onRemoveNew(idx)}
                      aria-label={t('common.delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}
    </Stack>
  );
}
